import type { TFunction } from 'i18next';
import { Change, HistoryIndex, Tone, changeOver, directionOf, scoreBand, signed } from '../detail';
import type { TileDelta } from '../widgets/MetricTile';

/** The comparison window of every tile's delta - the mockups' "vs. letzte 4 Wochen". */
export const TILE_DELTA_DAYS = 28;

const shortDate = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.`;

export function deltaFromChange(
  t: TFunction,
  change: Change | undefined,
  higherIsBetter = true,
  unit = '',
): TileDelta | undefined {
  if (!change) return undefined;
  return {
    text: signed(change.delta, unit),
    direction: directionOf(change.delta),
    higherIsBetter,
    hint: change.partial
      ? t('report.detail.since', { date: shortDate(change.since) })
      : t('report.detail.vsWeeks', { count: Math.round(TILE_DELTA_DAYS / 7) }),
  };
}

/** A tile's change against four weeks ago, from the loaded history. */
export function tileDelta(
  t: TFunction,
  history: HistoryIndex,
  key: string,
  current: number | undefined,
  higherIsBetter = true,
): TileDelta | undefined {
  return deltaFromChange(t, changeOver(history.get(key), current, TILE_DELTA_DAYS), higherIsBetter);
}

/** A score's status in the health vocabulary (On Track / Watch / At Risk / Critical). */
export function scoreStatus(t: TFunction, value: number | undefined): { tone: Tone; label: string } | undefined {
  const band = scoreBand(value);
  return band ? { tone: band.tone, label: t(`report.health.status.${band.key}`) } : undefined;
}

export const pct = (value: number | undefined) => (typeof value === 'number' ? `${Math.round(value)}%` : '—');

export const count = (value: number | undefined) => (typeof value === 'number' ? `${Math.round(value)}` : '—');

export const formatDay = (iso?: string | null) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : '—');
